import { createContext } from "react";
import {
  Sun, NotebookPen, ListChecks, ShieldAlert, FolderOpen, ClipboardCheck, DollarSign, Trophy, UtensilsCrossed, BarChart3, FileBarChart,
  TrendingDown, History, Package, ScanLine, Tags, Truck, BookOpen, Trash2, Users, CalendarDays, GraduationCap, MessagesSquare, HeartHandshake,
  Globe, Settings, Lock, Bell, Plug, ShieldCheck, CreditCard, LifeBuoy, LineChart,
} from "lucide-react";

export const WorkspaceCtx = createContext(null);

const L = (id, label, to, rule = {}) => ({ id, label, to, ...rule });
const W = (id, label, icon, views) => ({ id, label, icon, views });
const MGR = ["owner", "gm", "agm", "dept"];

// One hierarchy for desktop, mobile, search and route gating: section › workspace › view.
export const SECTIONS = [
  { id: "today", label: "Today", icon: Sun, children: [
    W("today", "Today", Sun, [
      L("overview", "Overview", "/app/dashboard", { module: "dashboard" }),
      L("current-shift", "Current shift", "/app/today", { open: true }),
      L("group-view", "Group view", "/app/group", { module: "group", scope: "group" }),
    ]),
  ] },
  { id: "shift-ops", label: "Shift Operations", icon: NotebookPen, children: [
    W("shift-record", "Shift Record", NotebookPen, [
      L("log", "Log", "/app/log", { module: "log" }),
      L("handoffs", "Handoffs", "/app/handoff", { module: "log" }),
      L("updates", "Updates", "/app/feed", { module: "feed" }),
    ]),
    W("work-queue", "Work Queue", ListChecks, [
      L("tasks", "Tasks", "/app/tasks", { module: "tasks" }),
      L("checklists", "Checklists", "/app/checklists", { module: "checklists" }),
    ]),
    W("exceptions", "Exceptions", ShieldAlert, [L("incidents", "Incidents", "/app/incidents", { module: "incidents" })]),
    W("resources", "Resources", FolderOpen, [L("documents", "Documents", "/app/documents", { module: "documents" })]),
  ] },
  { id: "close", label: "Close Shift", icon: ClipboardCheck, children: [
    W("close", "Close Shift", ClipboardCheck, [
      L("shift-close", "Close & history", "/app/close", { module: "log", roles: MGR }),
      L("tips", "Tip review", "/app/tips", { module: "tips" }),
      L("nightly", "Nightly reconciliation", "/app/tips/nightly", { module: "tips", keywords: "tips nightly close" }),
      L("tip-reports", "Tip reports", "/app/tips/reports", { module: "tips" }),
    ]),
  ] },
  { id: "sales", label: "Sales & Reports", icon: BarChart3, children: [
    W("sales-daily", "Sales", DollarSign, [L("daily-sales", "Daily Sales", "/app/sales", { module: "sales", match: ["/app/settings/sales-setup"] })]),
    W("sales-team", "Team", Trophy, [L("team-sales", "Team Sales", "/app/team-sales", { module: "sales" })]),
    W("sales-menu", "Menu", UtensilsCrossed, [L("product-mix", "Product Mix", "/app/product-mix", { module: "sales" })]),
    W("reports", "Reports", BarChart3, [L("report-families", "Reports", "/app/reports", { module: "reports" })]),
    W("saved-reports", "Saved Reports", FileBarChart, [L("weekly-owner", "Weekly Owner Report", "/app/report", { module: "owner_report" })]),
    W("insights", "Insights", TrendingDown, [L("money-leaks", "Money Leaks", "/app/leaks", { module: "leaks" })]),
    W("corrections", "Corrections", History, [L("data-corrections", "Data Corrections", "/app/data-corrections", { module: "sales", roles: ["owner", "gm", "agm"] })]),
  ] },
  { id: "inventory", label: "Inventory", icon: Package, children: [
    W("inv-overview", "Overview", LineChart, [
      L("inv-home", "Overview", "/app/inventory", { module: "inventory", exact: true }),
      L("inv-reports", "Trends & reports", "/app/inventory/reports", { module: "inventory" }),
      L("inv-targets", "Targets, sales & POS", "/app/inventory/sales", { module: "inventory" }),
      L("inv-sync", "App connection & sync", "/app/inventory/app-sync", { module: "inventory" }),
      L("inv-app-food", "Food Inventory · 86 app", "/app/inventory/app-access/food", { module: "inventory", keywords: "inventory app login password credentials food" }),
      L("inv-app-bev", "Beverage Inventory · 86 app", "/app/inventory/app-access/beverage", { module: "inventory", keywords: "inventory app login password credentials beverage bar" }),
      L("inv-settings", "Scopes & data ownership", "/app/inventory/settings", { module: "inventory" }),
    ]),
    W("inv-counts", "Counts", ScanLine, [
      L("inv-sessions", "Count sync & history", "/app/inventory/count", { module: "inventory", keywords: "counts sessions inventory app" }),
      L("inv-sheets", "Count sheets", "/app/inventory/sheets", { module: "inventory" }),
      L("inv-categories", "Categories", "/app/inventory/categories", { module: "inventory" }),
    ]),
    W("inv-products", "Products", Tags, [
      L("inv-product-list", "Product list", "/app/inventory/products", { module: "inventory" }),
      L("inv-counted", "Counted items & app", "/app/inventory/counted", { module: "inventory" }),
      L("inv-duplicates", "Duplicate review", "/app/inventory/duplicates", { module: "inventory" }),
    ]),
    W("inv-receiving", "Receiving", Truck, [
      L("inv-invoices", "Invoices", "/app/inventory/invoices", { module: "inventory" }),
      L("inv-orders", "Purchasing", "/app/inventory/orders", { module: "inventory" }),
      L("inv-vendors", "Vendors", "/app/inventory/vendors", { module: "inventory" }),
      L("inv-mapping", "Mapping queue", "/app/inventory/mapping", { module: "inventory" }),
    ]),
    W("inv-recipes", "Recipes", BookOpen, [
      L("inv-recipe-admin", "Recipes & batches", "/app/inventory/recipes", { module: "inventory" }),
      L("recipe-viewer", "Recipe Viewer", "/app/recipes", { perm: ["recipes_portal", "view"] }),
    ]),
    W("inv-movements", "Movements", Trash2, [
      L("waste", "Waste", "/app/waste", { module: "waste" }),
      L("inv-variance", "Variance", "/app/inventory/variance", { module: "inventory" }),
      L("inv-actions", "Investigation tasks", "/app/inventory/actions", { module: "inventory" }),
    ]),
  ] },
  { id: "people", label: "People", icon: Users, children: [
    W("people-access", "People & Access", Users, [
      L("directory", "Employee Directory", "/app/employees", { module: "employees", match: ["/app/people/"], keywords: "import employees staff" }),
      L("team-members", "Team Members", "/app/team", { module: "team" }),
    ]),
    W("schedule", "Schedule", CalendarDays, [L("schedule-view", "Schedule", "/app/schedule", { module: "employees" })]),
    W("onboarding", "Onboarding", GraduationCap, [L("onboarding-view", "Onboarding", "/app/onboarding", { module: "employees" })]),
    W("messages", "Messages", MessagesSquare, [L("inbox", "Management Inbox", "/app/inbox", { perm: ["messages_inbox", "view"], badge: "inbox" })]),
  ] },
  { id: "events", label: "Events & Guests", icon: CalendarDays, children: [
    W("events-beo", "Events", CalendarDays, [L("beo", "Events / BEOs", "/app/beo", { module: "beo" })]),
    W("guests", "Guests", HeartHandshake, [L("guest-profiles", "Guest Profiles", "/app/guests", { module: "sales" })]),
    W("public-profile", "Discover Profile", Globe, [L("marketing", "Discover Profile", "/app/marketing", { module: "settings", keywords: "marketing network public listing" })]),
  ] },
  { id: "settings", label: "Settings", icon: Settings, utility: true, children: [
    W("restaurant-setup", "Restaurant Setup", Settings, [L("builder", "Restaurant Builder", "/app/settings", { module: "settings", keywords: "tip policy tip-out configuration setup" })]),
    W("access", "Access", Lock, [
      L("permissions", "Permissions", "/app/access", { perm: ["access_manage", "edit"] }),
      L("report-card", "Report Card settings", "/app/people/settings", { module: "billing" }),
    ]),
    W("notifications", "Notifications", Bell, [
      L("my-notifications", "My notifications", "/app/notifications", { open: true }),
      L("alert-rules", "Alert rules", "/app/alerts", { roles: ["owner"] }),
    ]),
    W("integrations", "Integrations", Plug, [L("integration-center", "Integration Center", "/app/integrations", { module: "team", alt: "inventory", match: ["/app/settings/mobile-app"] })]),
    W("security-privacy", "Security & Privacy", ShieldCheck, [
      L("security", "Security & sessions", "/app/security", { module: "team" }),
      L("legal", "Legal & privacy", "/app/legal", { module: "team" }),
    ]),
    W("billing", "Billing", CreditCard, [L("billing-view", "Billing", "/app/billing", { module: "billing", match: ["/app/checkout"] })]),
  ] },
  { id: "help", label: "Help", icon: LifeBuoy, utility: true, children: [
    W("help", "Help", LifeBuoy, [L("help-support", "Help & Support", "/app/help", { module: "team" })]),
  ] },
];

const allowedView = (v, { can, allowed, has, user }) => {
  if (v.roles && !v.roles.includes(user?.role)) return false;
  if (v.perm && !has(...v.perm)) return false;
  if (v.module) return can(v.module) || !!(v.alt && can(v.alt)) || (user?.role === "owner" && allowed(v.module));
  return !!(v.perm || v.roles || v.open);
};

export const visibleSections = (auth) => SECTIONS.map((s) => ({
  ...s,
  children: s.children.map((c) => ({
    ...c,
    views: c.views.filter((v) => allowedView(v, auth)).map((v) => ({ ...v, locked: !!v.module && !auth.can(v.module) && !(v.alt && auth.can(v.alt)) })),
  })).filter((c) => c.views.length).map((c) => ({ ...c, to: (c.views.find((v) => !v.locked) || c.views[0]).to, badge: c.views.some((v) => v.badge), locked: c.views.every((v) => v.locked) })),
})).filter((s) => s.children.length).map((s) => ({ ...s, to: (s.children.find((c) => !c.locked) || s.children[0]).to }));

const pathOf = (s, c, v) => [s.label, c.label !== s.label && c.label, (c.views.length > 1 || v.label !== c.label) && v.label].filter(Boolean).join(" › ");

export const allLeaves = (sections) => sections.flatMap((s) => s.children.flatMap((c) => c.views.map((v) => ({ ...v, section: s, child: c, path: pathOf(s, c, v), search: `${pathOf(s, c, v)} ${v.keywords || ""}`.toLowerCase() }))));

export function resolveNav(sections, path, withMatch = true) {
  let best = null;
  let len = -1;
  allLeaves(sections).forEach((l) => [l.to, ...(withMatch ? l.match || [] : [])].forEach((p) => {
    const hit = l.exact && p === l.to ? path === p : path === p || path.startsWith(p.endsWith("/") ? p : `${p}/`);
    if (hit && p.length > len) { best = l; len = p.length; }
  }));
  return best && { section: best.section, child: best.child, view: best };
}
